import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { UseFormGetValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as _ from 'lodash';

import RegisterHeader from './RegisterHeader';
import RegisterFooter from './RegisterFooter';
import BusinessForm from './BusinessForm';
import BusinessFormInstructions from './BusinessFormInstructions';
import ContactForm from './ContactForm';
import TripPreferencesForm from '../home/providers/TripPreferences/TripPreferencesForm';
import ContactFormInstructions from './ContactFormInstructions';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import * as referenceSlice from '../../data/reference.slice';
import { useAppSelector } from '../../hooks/useAppSelector';
import preferenceUtils from '../../services/preferenceUtils';
import * as userSlice from '../../data/user.slice';
import * as providerSlice from '../../data/provider.slice';
import providerUtils from '../../services/providerUtils';
import InsuranceForm from './InsuranceForm';
import { useTranslation } from 'react-i18next';
import InsuranceFormInstructions from './InsuranceFormInstructions';
import TripPreferencesFormInstructions from './TripPreferencesFormInstructions';
import authService from '../../services/authService';
import {
  OnboardingBusiness,
  OnboardingContact,
  OnboardingInsurance,
} from '../../data/models/provider-onboarding.model';
import { useLoggedInProviderId } from '../../hooks/useLoggedInProviderId';
import { Provider } from '../../data/models/provider.model';

const PREFIX = 'RegisterProviderDialog';

const classes = {
  content: `${PREFIX}-content`,
};

const RootDialog = styled(Dialog)(() => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: 20,
  },
}));

interface ControlRef {
  submitForm: () => void;
  getValues: UseFormGetValues<any>;
}

interface RegisterProviderDialogProps {
  open: boolean;
  canClose: boolean;
  close: () => void;
}

export default function RegisterProviderDialog({
  open,
  canClose,
  close,
}: RegisterProviderDialogProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const providerId = useLoggedInProviderId();

  const provider = useAppSelector(providerSlice.selectProvider) as Provider;
  const isSavingProvider = useAppSelector(providerSlice.selectSavingProvider);
  const isSavingTripPreferences = useAppSelector(
    providerSlice.selectSavingTripPreferences,
  );
  const savingError = useAppSelector(providerSlice.selectSavingError);
  const userInfo = useAppSelector(userSlice.selectUserInfo);
  const entityTypes = useAppSelector(referenceSlice.selectLegalEntityTypes);
  const entityStatuses = useAppSelector(
    referenceSlice.selectLegalEntityStatuses,
  );
  const providerTypes = useAppSelector(referenceSlice.selectTransortationTypes);
  const messagingMethods = useAppSelector(
    referenceSlice.selectMessagingMethods,
  );
  const stateCodes = useAppSelector(referenceSlice.selectStateCodes);
  const tripModes = useAppSelector(referenceSlice.selectTripModes);
  const insuranceStrengths = useAppSelector(
    referenceSlice.selectInsuranceStrengths,
  );
  const timeZones = useAppSelector(referenceSlice.selectTimeZones);
  const coverageAreas = useAppSelector(providerSlice.selectCoverageAreas);
  const frequencyDayOfWeeks = useAppSelector(
    referenceSlice.selectFrequencyDayOfWeeks,
  );

  const steps = ['Business', 'Contact', 'Insurance', 'Trip Preferences'];

  const businessFormRef = useRef<ControlRef>();
  const contactFormRef = useRef<ControlRef>();
  const insuranceFormRef = useRef<ControlRef>();

  const {
    control,
    formState,
    setValue,
    watch,
    reset,
    trigger,
    handleSubmit: handleSubmitTripPreferences,
  } = useForm({
    defaultValues: {
      ...preferenceUtils.getDefaults(null, frequencyDayOfWeeks, tripModes),
    },
  });

  const [defaultValues, setDefaultValues] = useState({});

  useEffect(() => {
    setDefaultValues({
      ...providerUtils.getDefaults(
        userInfo.onboardingUser?.onboardingInProcessJSON ?? {},
      ),
      TransportationProviderName:
        (userInfo.onboardingUser?.onboardingInProcessJSON
          ?.transportationProviderName ||
          userInfo.onboardingUser?.transportationProviderName) ??
        '',
      BillingSameAsOwner:
        userInfo.onboardingUser?.onboardingInProcessJSON?.billingSameAsOwner ??
        false,
      DispatchSameAsOwner:
        userInfo.onboardingUser?.onboardingInProcessJSON?.dispatchSameAsOwner ??
        false,
    });
  }, [userInfo]);

  useEffect(() => {
    if (provider && frequencyDayOfWeeks && tripModes) {
      setIsTripPreferencesDisabled(false);

      reset(preferenceUtils.getDefaults(null, frequencyDayOfWeeks, tripModes));
    }
  }, [provider, frequencyDayOfWeeks, tripModes]);

  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});
  const [hours, setHours] = useState<{ time: string; label: string }[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [isTripPreferencesDisabled, setIsTripPreferencesDisabled] =
    useState<boolean>(true);

  useEffect(() => {
    dispatch(referenceSlice.fetchLegalEntityTypesAsync());
    dispatch(referenceSlice.fetchLegalEntityStatusesAsync());
    dispatch(referenceSlice.fetchTransportationTypesAsync());
    dispatch(referenceSlice.fetchMessagingMethodsAsync());

    const hrs = preferenceUtils.generateTimeOptions();
    setHours(hrs);

    for (let i = 0; i < 3; i++) {
      setCompleted({ [i]: false });
    }
  }, []);

  useEffect(() => {
    if (!isSavingProvider && provider) {
      setActiveStep(3);
    }
  }, [isSavingProvider, provider]);

  useEffect(() => {
    if (!isSavingTripPreferences) {
      close();
    }
  }, [isSavingTripPreferences]);

  useEffect(() => {
    if (savingError) {
      setSnackbarMessage(savingError);
      setSnackbarOpen(true);
    }
  }, [savingError]);

  const totalSteps = () => {
    return steps.length;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      businessFormRef?.current?.submitForm();
    } else if (activeStep === 1) {
      contactFormRef?.current?.submitForm();
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setDefaultValues({
        ...defaultValues,
        ...contactFormRef.current?.getValues(),
      });
    }
    if (activeStep === 2) {
      setDefaultValues({
        ...defaultValues,
        ...insuranceFormRef.current?.getValues(),
      });
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setDefaultValues({
      ...defaultValues,
      ...businessFormRef.current?.getValues(),
      ...contactFormRef.current?.getValues(),
      ...insuranceFormRef.current?.getValues(),
    });

    setActiveStep(step);
  };

  const handleClose = () => {
    close();
  };

  const saveFormValues = () => {
    const businessFormValues = businessFormRef.current?.getValues();
    const contactFormValues = contactFormRef.current?.getValues();
    const insuranceFormValues = insuranceFormRef.current?.getValues();

    const values = {
      ...defaultValues,
      ...businessFormValues,
      ...contactFormValues,
      ...insuranceFormValues,
    };

    const fn = providerSlice.updateProviderOnboardingUserAsync(
      userInfo?.onboardingUser?.transportationProviderUserOnboardingID,
      {
        onboardingInProcessJSON: values
          ? {
              ...providerUtils.getProviderFromFormData(
                false,
                undefined,
                values,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                [],
              ),
              billingSameAsOwner: values.billingSameAsOwner,
              dispatchSameAsOwner: values.dispatchSameAsOwner,
              billingSameAsOffice: values.billingSameAsOffice,
              dispatchSameAsOffice: values.dispatchSameAsOffice,
            }
          : {},
      },
    );
    dispatch(fn);
  };

  const handleCloseAndSave = () => {
    setConfirmDialogOpen(false);
    saveFormValues();
    authService.logout().then(() => navigate('/login'));
  };

  const submitBusiness = (data: OnboardingBusiness) => {
    setDefaultValues({ ...defaultValues, ...data });
    setCompleted({ ...completed, 0: true });
    setActiveStep(1);
  };

  const submitContact = (data: OnboardingContact) => {
    setDefaultValues({ ...defaultValues, ...data });
    setCompleted({ ...completed, 1: true });
    setActiveStep(2);
  };

  const submitInsurance = (data: OnboardingInsurance) => {
    setCompleted({ ...completed, 2: true });
    submit({ ...defaultValues, ...data });
  };

  const submitTripPreferences = (values: any) => {
    const prefData = preferenceUtils.getPreferencesFromFormData(
      null,
      frequencyDayOfWeeks,
      values,
    );

    dispatch(
      providerSlice.updateTripPreferencesAsync(
        provider?.transportationProviderID ||
          values?.transportationProviderID ||
          providerId,
        prefData,
      ),
    );
  };

  const handleSubmit = async () => {
    if (activeStep === 2) {
      insuranceFormRef?.current?.submitForm();
    } else if (activeStep === 3) {
      const isValid = await trigger();
      if (!isValid) {
        setSnackbarMessage(t('onboard.requiredFields'));
        setSnackbarOpen(true);
      } else {
        handleSubmitTripPreferences(submitTripPreferences)();
      }
    }
  };

  const submit = async (values: any) => {
    const data = { ...values, IsActive: true };

    // Set address info
    if (data.BillingSameAsOffice) {
      data['BillingAddressLine1'] = data['PhysicalAddressLine1'];
      data['BillingAddressLine2'] = data['PhysicalAddressLine2'];
      data['BillingCity'] = data['PhysicalCity'];
      data['BillingState'] = data['PhysicalState'];
      data['BillingZipCode'] = data['PhysicalZipCode'];
    }

    if (data.DispatchSameAsOffice) {
      data['AddressLine1'] = data['PhysicalAddressLine1'];
      data['AddressLine2'] = data['PhysicalAddressLine2'];
      data['City'] = data['PhysicalCity'];
      data['State'] = data['PhysicalState'];
      data['ZipCode'] = data['PhysicalZipCode'];
    }

    // Set billing info
    if (data.BillingSameAsOwner) {
      data['BillingPhone'] = data['OwnerPhone'];
      data['BillingPhoneExtension'] = data['OwnerPhoneExtension'];
      data['BillingEmailAddress'] = data['OwnerEmailAddress'];
      data['BillingContactFirstName'] = data['OwnerFirstName'];
      data['BillingContactLastName'] = data['OwnerLastName'];
    }

    if (data.DispatchSameAsOwner) {
      data['DispatchPhone'] = data['OwnerPhone'];
      data['DispatchPhoneExtension'] = data['OwnerPhoneExtension'];
      data['EmailAddress'] = data['OwnerEmailAddress'];
      data['MainContactFirstName'] = data['OwnerFirstName'];
      data['MainContactLastName'] = data['OwnerLastName'];
    }

    const providerData = providerUtils.getProviderFromFormData(
      false,
      undefined,
      { ...data },
      provider?.emailAddresses,
      provider?.phoneNumbers,
      provider?.addresses,
      provider?.insurancePolicies,
      provider?.coverageAreas,
      provider?.isInternal,
      [],
    );

    const fn = providerSlice.createProviderAsync(providerData);

    dispatch(fn);
  };

  const sameHoursAllDays = watch('sameHoursAllDays');

  return (
    <RootDialog fullScreen open={open} onClose={handleClose}>
      <RegisterHeader
        steps={steps}
        activeStep={activeStep}
        completed={completed}
        step={handleStep}
        close={close}
        isSaving={isSavingProvider || isSavingTripPreferences}
        isLastStepDisabled={isTripPreferencesDisabled}
        hideCloseButton={!canClose}
      />
      {(isSavingProvider || isSavingTripPreferences) && <LinearProgress />}
      <DialogContent className={classes.content}>
        {activeStep === 0 && (
          <>
            <BusinessForm
              legalEntityTypes={entityTypes}
              legalEntityStatuses={entityStatuses}
              providerTypes={providerTypes}
              stateCodes={stateCodes}
              defaultValues={defaultValues}
              ref={businessFormRef}
              onSubmit={submitBusiness}
            />
            {isLarge && <BusinessFormInstructions />}
          </>
        )}

        {activeStep === 1 && (
          <>
            <ContactForm
              messagingMethods={messagingMethods}
              defaultValues={defaultValues}
              onSubmit={submitContact}
              ref={contactFormRef}
            />
            {isLarge && <ContactFormInstructions />}
          </>
        )}
        {activeStep === 2 && (
          <>
            <InsuranceForm
              insuranceStrengths={insuranceStrengths}
              defaultValues={defaultValues}
              onSubmit={submitInsurance}
              ref={insuranceFormRef}
            />
            {isLarge && <InsuranceFormInstructions />}
          </>
        )}
        {activeStep === 3 && (
          <>
            <TripPreferencesForm
              watch={watch}
              control={control}
              formState={formState}
              setValue={setValue}
              hours={hours}
              timeZones={timeZones}
              coverageAreas={coverageAreas}
              sameHoursAllDays={sameHoursAllDays}
              hasTitle
            />
            {isLarge && <TripPreferencesFormInstructions />}
          </>
        )}
      </DialogContent>
      {(isSavingProvider || isSavingTripPreferences) && <LinearProgress />}
      <RegisterFooter
        activeStep={activeStep}
        isFirst={activeStep === 0}
        isLast={activeStep === totalSteps() - 1}
        isSaving={isSavingProvider || isSavingTripPreferences}
        next={handleNext}
        previous={handleBack}
        submit={handleSubmit}
        saveAndClose={() => {
          if (activeStep === 3) {
            handleClose();
          } else {
            setConfirmDialogOpen(true);
          }
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error">
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDialogOpen} disableEscapeKeyDown={true}>
        <DialogTitle>{t('onboard.confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">
            {t('onboard.confirmationMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={handleCloseAndSave}>
            {t('common.yes')}
          </Button>
          <Button
            color="info"
            onClick={() => setConfirmDialogOpen(false)}
            autoFocus>
            {t('common.no')}
          </Button>
        </DialogActions>
      </Dialog>
    </RootDialog>
  );
}
